import React from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Image,
  Divider,
  Icon,
  List,
  ListItem,
  ListIcon,
  Card,
  CardBody,
  Badge,
  SimpleGrid,
  OrderedList,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Link,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tag,
} from '@chakra-ui/react';
import {
  FaCloud,
  FaSignOutAlt,
  FaGraduationCap,
  FaRoute,
  FaCube,
  FaLayerGroup,
  FaSlidersH,
  FaPlane,
  FaProjectDiagram,
  FaFan,
  FaCity,
  FaBolt,
  FaWaveSquare,
  FaTasks,
  FaFlask,
  FaCheckDouble,
  FaCalendarAlt,
  FaBook,
  FaLightbulb,
  FaCheckCircle,
  FaExclamationTriangle,
  FaLink,
  FaArrowRight,
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import Footer from './Footer';
import NavDropdowns from './NavDropdowns';

import coverImage from '../img/learning-guide-cover.png';
import parametersImage from '../img/learning-guide-parameters.png';
import systemsImage from '../img/learning-guide-systems.png';
import workflowImage from '../img/learning-guide-workflow.png';

/* Table of contents */
const SECTIONS = [
  { id: 'how-to-use', title: 'How to Use This Guide', icon: FaRoute },
  { id: 'what-pyfr-is', title: '1. What PyFR Is', icon: FaCube },
  { id: 'foundations', title: '2. Universal Foundations', icon: FaLayerGroup },
  { id: 'governing-system', title: '3. Selecting the System', icon: FaSlidersH },
  { id: 'external-aero', title: '4. External Aerodynamics', icon: FaPlane },
  { id: 'internal-flows', title: '5. Internal Flows', icon: FaProjectDiagram },
  { id: 'turbomachinery', title: '6. Turbomachinery & Propulsion', icon: FaFan },
  { id: 'wind-civil', title: '7. Wind & Civil Aerodynamics', icon: FaCity },
  { id: 'high-speed', title: '8. High-Speed & Shock Flows', icon: FaBolt },
  { id: 'unsteady', title: '9. Unsteady Flow & Aeroacoustics', icon: FaWaveSquare },
  { id: 'workflow', title: '10. A Reliable Workflow', icon: FaTasks },
  { id: 'practice', title: '11. Practice Simulations', icon: FaFlask },
  { id: 'verification', title: '12. Verification & Validation', icon: FaCheckDouble },
  { id: 'learning-plan', title: '13. Twelve-Week Plan', icon: FaCalendarAlt },
  { id: 'glossary', title: '14. Glossary', icon: FaBook },
  { id: 'references', title: 'References & Resources', icon: FaLink },
];

/* Field-specific sections 4-9 all share the same "level ladder" structure. */
const FIELD_SECTIONS = [
  {
    id: 'external-aero',
    number: 4,
    icon: FaPlane,
    title: 'External Aerodynamics: Aerospace, UAV, Defense, Automotive, and Motorsport',
    intro:
      'External aerodynamics asks how air moves around a body and how the pressure and viscous stresses produce forces, moments, separation, wakes, and noise.',
    levels: [
      {
        level: 'Simple',
        concepts: 'Airfoil/wing/body vocabulary; angle of attack; stagnation; suction; wake; lift and drag.',
        questions:
          'Visualize flow around a 2D airfoil or simplified vehicle section. Predict where high and low pressure should occur.',
        outputs: 'Velocity and pressure contours; streamlines; qualitative wake shape.',
      },
      {
        level: 'Core',
        concepts:
          'Boundary layer; no-slip wall; pressure drag versus skin-friction drag; separation; finite domain and far-field boundaries.',
        questions:
          'Run an incompressible cylinder and then a low-speed airfoil. Compare inviscid and viscous expectations.',
        outputs: 'Surface pressure; force history; CL/CD; separation location.',
      },
      {
        level: 'Medium',
        concepts:
          'Reynolds/Mach number; transition sensitivity; vortex shedding; stall; 3D tip vortices; ground effect; yaw/crosswind.',
        questions: 'NACA wing section, simplified drone fuselage, Ahmed-body-like vehicle, or rear-wing element.',
        outputs: 'Time-averaged forces; Cp distribution; wake deficit; Strouhal number.',
      },
      {
        level: 'Advanced',
        concepts:
          'High-order wall resolution; inflow turbulence; implicit LES/LES concepts; aeroelastic forcing; dynamic stability; spectral analysis.',
        questions: '3D wing/fuselage or re-entry-capsule wake with long-time statistics and force/moment spectra.',
        outputs: 'Turbulence statistics; spectra; coherent structures; uncertainty and convergence.',
      },
    ],
    useCases: [
      'Fixed-wing UAV wing sections and control surfaces',
      'Drone fuselage, motor pod, strut, landing gear, or sensor housing',
      'Automotive body, spoiler, diffuser, mirror, wheel-adjacent flow, and crosswind',
      'Missile, projectile, or re-entry-body aerodynamics on fixed geometry',
    ],
    limitation:
      'Most lift/drag decisions require a viscous model. A 2D result cannot capture wing-tip vortices, finite-span induced drag, or full vehicle three-dimensional wakes.',
  },
  {
    id: 'internal-flows',
    number: 5,
    icon: FaProjectDiagram,
    title: 'Internal Flows and Industrial Equipment',
    intro:
      'Internal-flow simulation predicts pressure loss, flow uniformity, recirculation, mixing, pulsation, and wall loading inside ducts, channels, manifolds, nozzles, valves, and equipment passages.',
    levels: [
      {
        level: 'Simple',
        concepts: 'Inlet, outlet, wall, cross-section, flow rate, pressure drop, and recirculation.',
        questions: 'Straight channel or Couette flow; inspect how velocity varies between walls.',
        outputs: 'Velocity profile; pressure variation; mass-flow consistency.',
      },
      {
        level: 'Core',
        concepts:
          'No-slip wall; developing flow; entrance length; laminar versus turbulent; contraction and expansion losses.',
        questions: 'Duct bend, sudden expansion, converging nozzle, or manifold branch.',
        outputs: 'Pressure drop; flow split; wall shear; recirculation zones.',
      },
      {
        level: 'Medium',
        concepts:
          'Secondary flow; swirl; jets in crossflow; pulsatile flow; mixing quality; loss coefficient; residence-time thinking.',
        questions: 'Fan duct, valve-like restriction, multi-outlet manifold, or cooling passage.',
        outputs: 'Total/static pressure; uniformity index; turbulence intensity; frequency response.',
      },
      {
        level: 'Advanced',
        concepts:
          'Scale-resolving separated internal flow; compressible choking; shock trains; complex mixed-element curved meshes; coupled source terms.',
        questions: 'High-speed nozzle/duct, ejector-like fixed geometry, or high-Re separated industrial passage.',
        outputs: 'Spectra; loss budgets; shock motion; statistically averaged non-uniformity.',
      },
    ],
    useCases: [
      'Ventilation and process ducts',
      'Pump/fan housings as fixed-flow passages',
      'Valves, bends, diffusers, manifolds, mixers, and nozzles',
      'Electronics or machinery airflow passages when solid heat conduction is treated separately',
    ],
    limitation:
      'Pressure drop depends on accurate inlet/outlet definitions and adequate near-wall and separation resolution. PyFR does not automatically provide full conjugate solid–fluid heat transfer.',
  },
  {
    id: 'turbomachinery',
    number: 6,
    icon: FaFan,
    title: 'Turbomachinery, Propulsion, and Energy Flows',
    intro:
      'This field examines flow through blades, vanes, ducts, intakes, compressors, turbines, fans, propulsors, jets, and energy-conversion passages.',
    levels: [
      {
        level: 'Simple',
        concepts: 'Blade as an airfoil; pressure side/suction side; inlet, exit, turning, and wake.',
        questions: 'Stationary cascade or blade section with prescribed inflow.',
        outputs: 'Pressure distribution; flow turning; wake thickness.',
      },
      {
        level: 'Core',
        concepts: 'Total pressure and temperature; mass flow; incidence; losses; choking; blade loading.',
        questions: 'Fixed blade passage, intake, diffuser, or nozzle.',
        outputs: 'Mass flow; pressure ratio; loss coefficient; blade force.',
      },
      {
        level: 'Medium',
        concepts:
          'Secondary flows; tip leakage concept; rotor/stator wakes; compressibility; shock–boundary-layer interaction; acoustic tones.',
        questions: 'High-speed stationary blade row or ducted-fan passage using an appropriate fixed-domain approximation.',
        outputs: 'Entropy/loss fields; wake profiles; shock position; unsteady loading.',
      },
      {
        level: 'Advanced',
        concepts:
          'Wall-resolved or wall-modelled scale-resolving simulation; multi-passage periodicity; synthetic turbulence; long-time phase/statistical analysis.',
        questions: 'Research-grade blade passage, jet, or intake with high-order curved mesh and GPU scaling.',
        outputs: 'Turbulence spectra; loss mechanisms; coherent structures; acoustic source indicators.',
      },
    ],
    useCases: [
      'Stationary blade cascades and passage aerodynamics',
      'Nozzles, intakes, diffusers, exhausts, and jets',
      'Ducted-fan or propulsor passages under fixed-geometry approximations',
      'Turbulent blade wakes and high-speed shock-containing passages',
    ],
    limitation:
      'Do not assume that a full rotating rotor–stator or multi-rotor sliding-mesh workflow is a standard capability. Confirm the exact motion/periodicity method before committing to a project.',
  },
  {
    id: 'wind-civil',
    number: 7,
    icon: FaCity,
    title: 'Wind Energy, Environmental Wind, and Civil/Building Aerodynamics',
    intro:
      'These low-speed flows focus on wind loads, wakes, pedestrian-level wind, ventilation, pollutant transport approximations, terrain/obstacle effects, and wind-energy interactions.',
    levels: [
      {
        level: 'Simple',
        concepts: 'Wind direction, speed, obstacle, shelter region, wake, pressure on surfaces.',
        questions: 'Flow around a 2D square/cylinder or simple building block.',
        outputs: 'Velocity contours; recirculation; surface pressure.',
      },
      {
        level: 'Core',
        concepts:
          'Atmospheric-like inlet profile concept; no-slip ground; blockage; domain size; crosswind; force coefficients.',
        questions: 'Single building, bridge-deck section, solar-panel row, or fixed wind-turbine support.',
        outputs: 'Mean wind speed; pressure coefficient; drag/lift; wake length.',
      },
      {
        level: 'Medium',
        concepts:
          'Turbulence intensity/length scale; gusts; vortex shedding; interference between buildings; urban-canyon flow.',
        questions: 'Building cluster, bridge cross-section, rooftop equipment, or fixed wind-turbine blade section.',
        outputs: 'Time-averaged/peak loads; spectra; comfort or ventilation proxies.',
      },
      {
        level: 'Advanced',
        concepts:
          'Scale-resolving urban or terrain flow; synthetic inflow turbulence; long statistical windows; aeroacoustic or fatigue-relevant loading.',
        questions: 'Complex building cluster or wind-farm component wake under selected neutral-flow assumptions.',
        outputs: 'Turbulence statistics; gust factors; load spectra; coherent wake structures.',
      },
    ],
    useCases: [
      'Wind loading on buildings, roofs, panels, signs, and bridge sections',
      'Pedestrian-level wind and ventilation-flow proxies',
      'Wind-turbine blade sections, towers, nacelles, and fixed-geometry wake studies',
      'Urban and industrial-site wind interaction',
    ],
    limitation:
      'A full atmospheric boundary layer can involve buoyancy, thermal stratification, humidity, roughness heterogeneity, and weather forcing. State clearly which of these are neglected or approximated.',
  },
  {
    id: 'high-speed',
    number: 8,
    icon: FaBolt,
    title: 'High-Speed, Shock-Dominated, and Re-entry-Type Flows',
    intro:
      'High-speed CFD deals with density and temperature changes, pressure waves, expansion fans, shock waves, and their interaction with boundary layers and wakes.',
    levels: [
      {
        level: 'Simple',
        concepts: 'Speed of sound, Mach number, compression, expansion, and shock as an abrupt change.',
        questions: 'Euler vortex and double-Mach-reflection benchmark.',
        outputs: 'Density, pressure, Mach contours; wave position.',
      },
      {
        level: 'Core',
        concepts:
          'Subsonic/supersonic inlet and outlet logic; total versus static quantities; inviscid versus viscous shock behaviour.',
        questions: 'Nozzle, wedge, shock tube, or supersonic body.',
        outputs: 'Shock angle/location; pressure ratios; mass flow.',
      },
      {
        level: 'Medium',
        concepts:
          'Shock capturing; artificial viscosity/entropy filtering; viscous heating; shock–boundary-layer interaction; separation.',
        questions: 'Viscous shock tube or supersonic airfoil/body with controlled mesh refinement.',
        outputs: 'Wall pressure/heat-related fluid variables; shock motion; separation bubble.',
      },
      {
        level: 'Advanced',
        concepts:
          'Shock/turbulence interaction; high-order stability; curved geometry; hypersonic/re-entry wake dynamics under verified gas assumptions.',
        questions: 'Research-grade shock-containing turbulent flow or capsule wake.',
        outputs: 'Spectra, unsteady forces/moments, shock statistics, grid/order sensitivity.',
      },
    ],
    useCases: [
      'Supersonic inlets, nozzles, jets, and shock tubes',
      'High-speed airfoils, projectiles, missiles, and fixed re-entry shapes',
      'Shock–boundary-layer and shock–turbulence interaction',
      'Unsteady high-speed wakes and dynamic-stability studies',
    ],
    limitation:
      'At extreme temperatures, real-gas chemistry, ionization, radiation, ablation, and material response may matter. The standard perfect-gas flow model must not be used beyond its physical validity without extensions.',
  },
  {
    id: 'unsteady',
    number: 9,
    icon: FaWaveSquare,
    title: 'Unsteady Flow, Turbulent Wakes, and Aeroacoustics',
    intro:
      'Unsteadiness is often the reason to use PyFR: vortices, turbulence, periodic shedding, force fluctuations, and acoustic-source behaviour are poorly represented by a single steady picture.',
    levels: [
      {
        level: 'Simple',
        concepts: 'A vortex; a wake; periodic motion; time history; frequency.',
        questions: 'Watch vortex shedding behind a cylinder.',
        outputs: 'Animation; force oscillation; dominant period.',
      },
      {
        level: 'Core',
        concepts: 'Transient startup; sampling rate; transient removal; Strouhal number; mean versus fluctuation.',
        questions: 'Compute cylinder drag/lift histories and estimate shedding frequency.',
        outputs: 'Mean/rms values; FFT peak; phase relationship.',
      },
      {
        level: 'Medium',
        concepts:
          'Turbulent energy cascade; coherent structures; autocorrelation; spectra; time averaging; synthetic inflow turbulence.',
        questions: '3D wake, aerofoil with turbulent inflow, or Taylor–Green vortex.',
        outputs: 'Turbulence statistics; spectra; Q/vorticity structures; convergence of averages.',
      },
      {
        level: 'Advanced',
        concepts: 'LES/DNS resolution; acoustic analogies; FWH surface/observer choice; bandwidth; windowing; statistical uncertainty.',
        questions: 'Aerofoil, bluff body, jet, or landing-gear-like fixed geometry with far-field noise approximation.',
        outputs: 'Sound pressure history/spectrum; source-region interpretation; uncertainty.',
      },
    ],
    useCases: [
      'Vortex shedding and buffet-like force oscillations',
      'Wake mixing and coherent structures',
      'Turbulent inflow and wake interaction',
      'Far-field aeroacoustic approximation using the FWH plugin',
    ],
    limitation:
      'A beautiful animation is not proof of accuracy. Sampling duration, time step, mesh/order resolution, domain boundaries, and acoustic-surface selection all affect spectral and noise results.',
  },
];

const PRACTICE_CASES = [
  { n: 1, case: '2D Euler vortex', level: 'Simple → Core', concepts: 'Convection, density/velocity fields, periodicity, numerical accuracy', system: 'euler' },
  { n: 2, case: '2D Couette flow', level: 'Simple → Core', concepts: 'Viscosity, no-slip wall, linear velocity profile, steady solution', system: 'navier-stokes' },
  { n: 3, case: '2D incompressible cylinder', level: 'Core → Medium', concepts: 'Boundary layer, separation, vortex shedding, force history', system: 'ac-navier-stokes' },
  { n: 4, case: '2D double Mach reflection', level: 'Core → Medium', concepts: 'Shock waves, reflections, discontinuities, shock capturing', system: 'euler' },
  { n: 5, case: '2D viscous shock tube', level: 'Medium', concepts: 'Compressible viscosity, shocks, contact structures, wall interaction', system: 'navier-stokes' },
  { n: 6, case: '3D Taylor–Green vortex', level: 'Medium → Advanced', concepts: 'Transition to turbulence, energy decay, 3D vortical structures', system: 'navier-stokes' },
  { n: 7, case: '3D triangular aerofoil', level: 'Medium → Advanced', concepts: 'Complex geometry, inflow turbulence, 3D wake and statistics', system: 'navier-stokes' },
  { n: 8, case: 'Custom NACA airfoil / cylinder crossflow', level: 'Medium', concepts: 'Engineering coefficients, mesh/domain study, comparison to references', system: 'ac-navier-stokes or navier-stokes' },
  { n: 9, case: 'Custom duct/nozzle/building', level: 'Medium → Advanced', concepts: 'Field-specific workflow, geometry simplification, decision metrics', system: 'Choose from Section 3' },
  { n: 10, case: 'Research-scale 3D turbulent case', level: 'Advanced', concepts: 'High order, long averaging, spectra, scaling, validation', system: 'Problem dependent' },
];

const WEEK_PLAN = [
  { week: 1, theme: 'Fluid intuition', activity: 'Velocity, pressure, viscosity, density, steady/unsteady; use simple sketches and videos.', deliverable: 'Explain a flow without equations.' },
  { week: 2, theme: 'CFD workflow', activity: 'Fluid domain, geometry, mesh, boundaries, initial conditions, solver, post-processing.', deliverable: 'Draw the workflow for a cylinder case.' },
  { week: 3, theme: 'Governing-system selection', activity: 'Compressible/incompressible; Euler/Navier–Stokes; wall choices.', deliverable: 'Select a system for ten example problems.' },
  { week: 4, theme: 'First PyFR workflow', activity: 'Files, import, run, export, ParaView; Euler vortex.', deliverable: 'Complete and document one official case.' },
  { week: 5, theme: 'Viscosity and walls', activity: 'Couette flow; no-slip; profiles; basic verification.', deliverable: 'Compare numerical and expected profile.' },
  { week: 6, theme: 'Unsteady wakes', activity: 'Incompressible cylinder; lift/drag history; shedding frequency.', deliverable: 'Calculate mean CD and Strouhal number.' },
  { week: 7, theme: 'External aerodynamics', activity: 'Airfoil/vehicle concepts; Cp, CL, CD, separation.', deliverable: 'Run a 2D airfoil or simplified body.' },
  { week: 8, theme: 'Internal flow', activity: 'Pressure loss, mass flow, recirculation, duct/nozzle.', deliverable: 'Create a decision-focused internal-flow report.' },
  { week: 9, theme: 'Compressible flow', activity: 'Mach number, shocks, shock tube/double reflection.', deliverable: 'Identify shock-capturing and boundary needs.' },
  { week: 10, theme: 'Turbulence and statistics', activity: 'Taylor–Green; averaging; spectra; LES/DNS concepts.', deliverable: 'Produce mean/rms/spectral interpretation.' },
  { week: 11, theme: 'Verification and validation', activity: 'Mesh/order/time/domain studies; comparison with references.', deliverable: 'Write an uncertainty and credibility section.' },
  { week: 12, theme: 'Field capstone', activity: 'Choose aerospace, automotive, industrial, propulsion, wind, or civil use case.', deliverable: 'Deliver a concise simulation specification before running.' },
];

const GLOSSARY = [
  ['Adiabatic wall', 'A wall through which no heat flux is imposed in the fluid model.'],
  ['Aliasing', 'Numerical error from representing nonlinear products with insufficient quadrature/resolution.'],
  ['Angle of attack', 'Angle between a reference body/airfoil direction and the incoming flow.'],
  ['Boundary condition', 'A mathematical rule applied at the edge of the fluid domain.'],
  ['Boundary layer', 'Thin near-wall region in which viscous effects and velocity gradients are important.'],
  ['CFD', 'Computational Fluid Dynamics: numerical solution of fluid-flow equations.'],
  ['Compressible flow', 'Flow in which density variation and pressure-wave effects are retained.'],
  ['Convergence', 'Approach toward a stable numerical/statistical result; context must be stated.'],
  ['DNS', 'Direct Numerical Simulation, resolving all dynamically relevant turbulence scales.'],
  ['Drag', 'Force component parallel to the reference incoming flow.'],
  ['Euler equations', 'Conservation equations for inviscid compressible flow.'],
  ['Flux Reconstruction', 'High-order method used by PyFR to represent and communicate fluxes across elements.'],
  ['FWH', 'Ffowcs Williams–Hawkings acoustic analogy used to approximate far-field noise.'],
  ['High-order method', 'A numerical scheme capable of reducing error rapidly with polynomial order for smooth solutions.'],
  ['Incompressible flow', 'A model in which density is treated as constant or divergence-free velocity is enforced.'],
  ['LES', 'Large-Eddy Simulation, resolving large turbulent motions and modelling/dissipating smaller ones.'],
  ['Lift', 'Force component perpendicular to the reference incoming flow.'],
  ['Mach number', 'Flow speed divided by speed of sound.'],
  ['Mesh', 'Collection of elements representing the computational domain.'],
  ['Navier–Stokes equations', 'Conservation equations that include viscous stresses and, for compressible form, energy.'],
  ['No-slip wall', 'A viscous wall condition where fluid velocity matches wall velocity.'],
  ['Polynomial order', 'Degree of the solution representation inside an element.'],
  ['Pressure coefficient', 'Normalized pressure relative to a reference free stream.'],
  ['Reynolds number', 'Ratio comparing inertia with viscosity.'],
  ['Residual', 'A measure related to how well the discrete equations are being satisfied or updated.'],
  ['Shock', 'Very thin compressive wave across which flow properties change abruptly.'],
  ['Slip wall', 'Wall condition preventing normal flow while allowing tangential motion; no viscous shear.'],
  ['Strouhal number', 'Nondimensional oscillation frequency, often used for vortex shedding.'],
  ['Turbulence', 'Irregular, multi-scale, three-dimensional unsteady fluid motion.'],
  ['Validation', 'Assessment of whether the model represents physical reality sufficiently.'],
  ['Verification', 'Assessment of numerical accuracy for the equations and setup being solved.'],
  ['Vorticity', 'Measure of local fluid rotation.'],
  ['Wake', 'Downstream region disturbed by an object or upstream component.'],
];

const REFERENCES = [
  { label: 'PyFR Documentation, stable release (PyFR 3.1).', url: 'https://pyfr.readthedocs.io/en/stable/' },
  { label: 'PyFR Examples: Euler, Couette, incompressible cylinder, viscous shock tube, triangular aerofoil, Taylor–Green.', url: 'https://pyfr.readthedocs.io/en/latest/examples.html' },
  { label: 'PyFR solver configuration: documented systems, order, anti-aliasing, and shock-capturing options.', url: 'https://pyfr.readthedocs.io/en/stable/systems/solver.html' },
  { label: 'PyFR User Guide: files, commands, backends, plugins, and parallel operation.', url: 'https://pyfr.readthedocs.io/en/stable/user_guide.html' },
  { label: 'Witherden et al., "PyFR v2.0.3: Towards industrial adoption of scale-resolving simulations," Computer Physics Communications, 2025.', url: 'https://www.sciencedirect.com/science/article/pii/S0010465525000700' },
  { label: 'PyFR fluid-force plugin.', url: 'https://pyfr.readthedocs.io/en/stable/plugins/soln-plugin-fluid-force.html' },
  { label: 'PyFR Ffowcs Williams–Hawkings plugin.', url: 'https://pyfr.readthedocs.io/en/stable/plugins/soln-plugin-fwh.html' },
  { label: 'PyFR time-average plugin.', url: 'https://pyfr.readthedocs.io/en/stable/plugins/soln-plugin-tavg.html' },
  { label: 'PyFR synthetic-eddy turbulence plugin.', url: 'https://pyfr.readthedocs.io/en/stable/plugins/solver-plugin-turbulence.html' },
  { label: 'PyFR project website.', url: 'https://www.pyfr.org/' },
];

const LEVEL_COLORS = {
  Simple: 'green',
  Core: 'blue',
  Medium: 'orange',
  Advanced: 'purple',
};

/* A callout box that mirrors the single-cell highlight tables in the source doc. */
const Callout = ({ title, children, status = 'info', icon = FaLightbulb }) => (
  <Alert status={status} borderRadius="md" alignItems="start">
    <AlertIcon as={icon} mt={1} />
    <Box>
      {title && <AlertTitle>{title}</AlertTitle>}
      <AlertDescription>{children}</AlertDescription>
    </Box>
  </Alert>
);

const BulletList = ({ items }) => (
  <List spacing={3}>
    {items.map((item, idx) => (
      <ListItem key={idx} display="flex" alignItems="start">
        <ListIcon as={FaCheckCircle} color="brand.500" mt={1} />
        <Text color="gray.700">{item}</Text>
      </ListItem>
    ))}
  </List>
);

const FieldSection = ({ section }) => (
  <Box id={section.id}>
    <VStack align="stretch" spacing={6}>
      <HStack>
        <Icon as={section.icon} color="brand.500" boxSize={8} />
        <Heading size="xl" color="gray.800">
          {section.number}. {section.title}
        </Heading>
      </HStack>

      <Text fontSize="lg" color="gray.700" lineHeight="tall">
        {section.intro}
      </Text>

      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Level</Th>
              <Th>Concepts to learn</Th>
              <Th>Questions / practice cases</Th>
              <Th>Outputs to understand</Th>
            </Tr>
          </Thead>
          <Tbody>
            {section.levels.map((row) => (
              <Tr key={row.level}>
                <Td verticalAlign="top">
                  <Badge colorScheme={LEVEL_COLORS[row.level]}>{row.level}</Badge>
                </Td>
                <Td verticalAlign="top" whiteSpace="normal">{row.concepts}</Td>
                <Td verticalAlign="top" whiteSpace="normal">{row.questions}</Td>
                <Td verticalAlign="top" whiteSpace="normal">{row.outputs}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      <Card bg="gray.50">
        <CardBody>
          <Heading size="sm" mb={4} color="gray.800">Representative use cases</Heading>
          <BulletList items={section.useCases} />
        </CardBody>
      </Card>

      <Callout title="Important limitation" status="warning" icon={FaExclamationTriangle}>
        {section.limitation}
      </Callout>
    </VStack>
  </Box>
);

const LearningGuide = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Box>
      <Helmet>
        <title>PyFR Learning Guide — Beginner to Advanced | SRS</title>
        <meta
          name="description"
          content="A beginner-to-advanced roadmap for learning fluid simulation with PyFR on SRS — covering aerospace, automotive, propulsion, wind, civil and industrial flows."
        />
        <link rel="canonical" href="https://srs.numericalai.net/learning-guide" />
      </Helmet>

      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200" position="sticky" top={0} zIndex={10}>
        <Container maxW="container.xl" py={4}>
          <HStack justify="space-between">
            <HStack
              spacing={1}
              cursor="pointer"
              onClick={() => navigate('/')}
              _hover={{ opacity: 0.8 }}
              transition="opacity 0.2s"
              borderRadius="md"
              p={2}
              _active={{ transform: 'scale(0.98)' }}
            >
              <Image src="/simple-logo.png" alt="SRS Logo" boxSize={14} objectFit="contain" />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <HStack spacing={4}>
              <NavDropdowns />
              {user ? (
                <Button leftIcon={<FaCloud />} colorScheme="brand" size="sm" variant="ghost" as={RouterLink} to="/dashboard">
                  Dashboard
                </Button>
              ) : (
                <Button variant="ghost" as={RouterLink} to="/signin">
                  Sign In
                </Button>
              )}
              {user ? (
                <Button leftIcon={<FaSignOutAlt />} variant="outline" size="sm" onClick={() => logout()}>
                  Sign Out
                </Button>
              ) : (
                <Button colorScheme="brand" as={RouterLink} to="/create-user">
                  Get Started
                </Button>
              )}
            </HStack>
          </HStack>
        </Container>
      </Box>

      {/* Hero */}
      <Box bg="linear-gradient(135deg, #0891b2 0%, #155e75 100%)" color="white" py={12}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center">
            <Icon as={FaGraduationCap} w={16} h={16} />
            <Heading as="h1" size="2xl">Learning Fluid Simulation with PyFR</Heading>
            <Text fontSize="xl" maxW="3xl" opacity={0.9}>
              A novice-friendly, beginner-to-advanced roadmap across aerospace, automotive, propulsion, wind,
              civil, and industrial flow simulation.
            </Text>
            <HStack spacing={3} pt={2} flexWrap="wrap" justify="center">
              {['Simple', 'Core', 'Medium', 'Advanced'].map((lvl, idx) => (
                <React.Fragment key={lvl}>
                  <Tag size="lg" bg="whiteAlpha.300" color="white" borderRadius="full" px={4}>
                    {lvl}
                  </Tag>
                  {idx < 3 && <Icon as={FaArrowRight} boxSize={3} opacity={0.7} />}
                </React.Fragment>
              ))}
            </HStack>
            <Text fontSize="sm" opacity={0.75} pt={2}>
              Prepared for NumericalAI / PyFR onboarding · Edition: July 2026
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="container.xl" py={12}>
        <SimpleGrid columns={{ base: 1, lg: 4 }} spacing={8}>
          {/* Sidebar */}
          <Box position={{ base: 'relative', lg: 'sticky' }} top={{ lg: '100px' }} height="fit-content">
            <Card>
              <CardBody>
                <VStack align="stretch" spacing={4}>
                  <Heading size="md" color="gray.800">Table of Contents</Heading>
                  <Divider />
                  <VStack align="stretch" spacing={1}>
                    {SECTIONS.map((section) => (
                      <Button
                        key={section.id}
                        leftIcon={<Icon as={section.icon} />}
                        variant="ghost"
                        justifyContent="flex-start"
                        onClick={() => scrollToSection(section.id)}
                        size="sm"
                        fontSize="xs"
                        textAlign="left"
                        whiteSpace="normal"
                        height="auto"
                        py={2}
                      >
                        {section.title}
                      </Button>
                    ))}
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          </Box>

          {/* Content */}
          <Box gridColumn={{ base: 'span 1', lg: 'span 3' }}>
            <VStack spacing={12} align="stretch">
              {/* Cover image + intro */}
              <Box>
                <Image
                  src={coverImage}
                  alt="The PyFR learning ladder: Simple, Core, Medium, and Advanced levels"
                  borderRadius="lg"
                  border="1px"
                  borderColor="gray.200"
                  mb={6}
                />
                <Callout status="info" icon={FaRoute}>
                  This guide assumes you are new to computational fluid dynamics (CFD). It does not begin with
                  equations or PyFR configuration syntax. Instead, it first builds a mental model of fluid flow,
                  then introduces the physical and numerical ideas required to set up credible simulations.
                </Callout>
              </Box>

              {/* How to Use */}
              <Box id="how-to-use">
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaRoute} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">How to Use This Guide</Heading>
                  </HStack>

                  <Callout title="Recommended learning rule" status="success" icon={FaLightbulb}>
                    Do not move to a higher level merely because you can run a case. Move forward when you can
                    explain what the case represents, predict the main flow pattern, identify the important
                    outputs, and recognize an obviously incorrect result.
                  </Callout>

                  <Text fontSize="lg" color="gray.700" lineHeight="tall">
                    The sequence used throughout the document is:
                  </Text>

                  <Card>
                    <CardBody>
                      <OrderedList spacing={4} fontSize="md">
                        <ListItem>
                          <Text as="span" fontWeight="semibold">Simple:</Text> recognize the physical situation and the main quantities.
                        </ListItem>
                        <ListItem>
                          <Text as="span" fontWeight="semibold">Core:</Text> understand the governing physics, boundaries, mesh, and time dependence.
                        </ListItem>
                        <ListItem>
                          <Text as="span" fontWeight="semibold">Medium:</Text> interpret nondimensional parameters, forces, unsteadiness, and numerical sensitivity.
                        </ListItem>
                        <ListItem>
                          <Text as="span" fontWeight="semibold">Advanced:</Text> undertake high-order, scale-resolving, shock-containing, statistically converged, or large parallel simulations.
                        </ListItem>
                      </OrderedList>
                    </CardBody>
                  </Card>
                </VStack>
              </Box>

              <Divider />

              {/* 1. What PyFR Is */}
              <Box id="what-pyfr-is">
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaCube} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">1. What PyFR Is — and What It Is Not</Heading>
                  </HStack>

                  <Text fontSize="lg" color="gray.700" lineHeight="tall">
                    PyFR is an open-source CFD solver that uses a high-order Flux Reconstruction method on
                    unstructured meshes. Its present documented governing systems are compressible Euler,
                    compressible Navier–Stokes, artificial-compressibility Euler, and artificial-compressibility
                    Navier–Stokes. These cover inviscid and viscous gas or liquid flow, including unsteady and
                    turbulent flow problems.
                  </Text>

                  <Callout title="PyFR in one sentence" status="info" icon={FaCube}>
                    PyFR predicts how a fluid's velocity, pressure, density, and — when using the compressible
                    equations — energy change through space and time around or inside engineering geometry.
                  </Callout>

                  <Heading size="md" color="gray.800">1.2 Why PyFR is distinctive</Heading>
                  <BulletList
                    items={[
                      'High-order accuracy: each mesh element can represent a curved and smoothly varying solution with a polynomial rather than a single cell-average value.',
                      'Scale-resolving focus: it is particularly suited to unsteady turbulent structures, wakes, vortices, and flow features for which excessive numerical dissipation is undesirable.',
                      'Unstructured mixed-element meshes: useful for complex engineering geometries.',
                      'Cross-platform execution: current documentation describes CPU and multiple accelerator backends, including CUDA, HIP, OpenCL, Metal, and OpenMP.',
                      'Parallel operation: meshes can be partitioned and cases can run across multiple processes/devices.',
                    ]}
                  />

                  <Heading size="md" color="gray.800">1.3 What is directly in scope</Heading>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Simulation family</Th>
                          <Th>Typical examples</Th>
                          <Th>Relevant PyFR system</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {[
                          ['Low-speed viscous flow', 'Cylinder wakes, ducts, building wind, vehicle wakes', 'ac-navier-stokes'],
                          ['Low-speed inviscid idealization', 'Potential-like benchmark flows where wall shear is ignored', 'ac-euler'],
                          ['Compressible inviscid flow', 'Vortices, expansion waves, idealized shock problems', 'euler'],
                          ['Compressible viscous flow', 'High-speed boundary layers, jets, shock–boundary-layer interaction', 'navier-stokes'],
                          ['Unsteady force prediction', 'Lift, drag, side force, moment, vortex shedding', 'Fluid-force plugin plus a flow system'],
                          ['Far-field noise approximation', 'Noise estimated from an enclosing surface', 'Ffowcs Williams–Hawkings plugin'],
                        ].map((row) => (
                          <Tr key={row[0]}>
                            <Td verticalAlign="top" whiteSpace="normal">{row[0]}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row[1]}</Td>
                            <Td verticalAlign="top" whiteSpace="normal"><Text as="code" fontSize="xs">{row[2]}</Text></Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>

                  <Heading size="md" color="gray.800">1.4 Areas that require caution or another solver</Heading>
                  <Text color="gray.700">
                    PyFR is primarily a fluid-flow solver. The following are not standard out-of-the-box workflows
                    in the current user documentation and should not be promised as ordinary PyFR capabilities
                    without a specific extension, coupling strategy, or development effort:
                  </Text>
                  <BulletList
                    items={[
                      'Structural stress, deformation, fatigue, or crash mechanics.',
                      'Electromagnetics, RF, antennas, quantum-device fields, or circuit simulation.',
                      'Full conjugate heat transfer through both fluids and solid structures.',
                      'Multiphase free surfaces, droplets, cavitation, or particle-laden flow as standard models.',
                      'Detailed combustion chemistry and reacting species transport as a standard model.',
                      'Complete meteorology with atmospheric stratification, humidity, radiation, and weather physics.',
                      'True moving/sliding meshes for complete rotor–stator or multi-rotor motion unless a verified PyFR-specific method or extension is available.',
                    ]}
                  />

                  <Callout title="Commercial positioning principle" status="warning" icon={FaExclamationTriangle}>
                    Describe PyFR as a high-fidelity fluid-dynamics capability. Do not describe it as a
                    general-purpose multiphysics platform by itself. NumericalAI may combine PyFR with other
                    solvers for a broader offering.
                  </Callout>
                </VStack>
              </Box>

              <Divider />

              {/* 2. Universal Foundations */}
              <Box id="foundations">
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaLayerGroup} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">2. Universal Foundations for Every CFD Field</Heading>
                  </HStack>

                  <Heading size="md" color="gray.800">2.1 Simple concepts: learn to describe the flow</Heading>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Concept</Th>
                          <Th>Plain-language meaning</Th>
                          <Th>What to look for in a result</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {[
                          ['Fluid domain', 'The space occupied by air, water, or another fluid.', 'Is the simulated region large enough and connected correctly?'],
                          ['Velocity', 'Speed and direction of the fluid.', 'Fast regions, slow regions, recirculation, and direction changes.'],
                          ['Pressure', 'Normal force per unit area exerted by the fluid.', 'High-pressure stagnation zones and low-pressure suction zones.'],
                          ['Density', 'Mass per unit volume.', 'Nearly constant in low-speed flows; strongly variable in compressible flows.'],
                          ['Viscosity', 'Resistance to shearing motion.', 'Wall friction, boundary layers, diffusion of velocity gradients.'],
                          ['Steady flow', 'The statistical or exact solution does not change with time.', 'A fixed field or stable mean.'],
                          ['Unsteady flow', 'The flow changes with time.', 'Oscillating forces, moving vortices, waves, and transient events.'],
                          ['External flow', 'Fluid moves around an object.', 'Wings, cars, buildings, cylinders, blades.'],
                          ['Internal flow', 'Fluid moves through a passage.', 'Ducts, pipes, nozzles, fans, valves, heat-exchanger passages.'],
                        ].map((row) => (
                          <Tr key={row[0]}>
                            <Td verticalAlign="top" fontWeight="semibold" whiteSpace="normal">{row[0]}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row[1]}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row[2]}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>

                  <Heading size="md" color="gray.800">2.2 Core concepts: understand why the flow behaves that way</Heading>
                  <BulletList
                    items={[
                      'Conservation of mass: fluid mass cannot appear or disappear inside the domain unless a source is deliberately introduced.',
                      'Conservation of momentum: pressure, viscosity, and body forces change the fluid\u2019s motion.',
                      'Conservation of energy: important for compressible flows where temperature, pressure, density, and speed are coupled.',
                      'Inviscid versus viscous: Euler equations omit viscous stresses; Navier–Stokes equations include them.',
                      'Compressible versus incompressible: density variations are retained in compressible modelling and suppressed through an incompressible formulation when appropriate.',
                      'Initial conditions: the flow state at simulation start.',
                      'Boundary conditions: rules applied at inlets, outlets, walls, far-field boundaries, symmetry planes, and periodic pairs.',
                      'Mesh: the geometric partition on which the solution is represented.',
                      'Time step: the increment through which an unsteady solution advances.',
                    ]}
                  />

                  <Heading size="md" color="gray.800">2.3 Medium concepts: learn the organizing parameters</Heading>
                  <Image
                    src={parametersImage}
                    alt="Reynolds number versus Mach number map showing where compressibility and turbulence matter"
                    borderRadius="lg"
                    border="1px"
                    borderColor="gray.200"
                  />
                  <Text color="gray.700">A small set of nondimensional numbers organizes a large fraction of engineering CFD:</Text>
                  <BulletList
                    items={[
                      'Reynolds number, Re = ρUL/μ: compares inertial effects with viscous effects. Higher values usually produce thinner boundary layers, stronger separation sensitivity, and more turbulent scales.',
                      'Mach number, Ma = U/a: compares flow speed with the speed of sound. It indicates when compressibility, wave propagation, and shocks may matter.',
                      'Strouhal number, St = fL/U: describes the nondimensional frequency of periodic unsteadiness such as vortex shedding.',
                      'Pressure coefficient, Cp: presents surface pressure in a normalized form useful for comparing geometries and speeds.',
                      'Lift and drag coefficients, CL and CD: normalize aerodynamic forces so designs can be compared.',
                    ]}
                  />

                  <Heading size="md" color="gray.800">2.4 Advanced concepts: understand high-fidelity simulation</Heading>
                  <BulletList
                    items={[
                      'Spatial order (p-order): the polynomial order used inside each element. Raising order can increase accuracy without changing the element count, but increases cost and may expose mesh-quality problems.',
                      'Flux Reconstruction: a high-order discretization framework that reconstructs fluxes and corrects them to communicate consistently across element interfaces.',
                      'Direct Numerical Simulation (DNS): resolves all dynamically relevant turbulence scales; extremely expensive at engineering Reynolds numbers.',
                      'Large-Eddy Simulation (LES) and implicit LES: resolve large turbulent structures while modelling or numerically dissipating smaller scales.',
                      'Aliasing: errors caused by nonlinear terms being under-integrated. Anti-aliasing and adequate quadrature can improve robustness and accuracy.',
                      'Shock capturing: stabilization for discontinuities; current PyFR options include entropy filtering and artificial viscosity for relevant compressible systems.',
                      'Statistical convergence: mean values and spectra are meaningful only after transients have passed and sufficient independent flow events have been sampled.',
                      'Scalability: how efficiently a case uses more GPUs/CPUs; partition balance, communication, and element mix matter.',
                    ]}
                  />
                </VStack>
              </Box>

              <Divider />

              {/* 3. Selecting the Governing System */}
              <Box id="governing-system">
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaSlidersH} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">3. Selecting the PyFR Governing System</Heading>
                  </HStack>

                  <Image
                    src={systemsImage}
                    alt="Decision flowchart for choosing between ac-euler, ac-navier-stokes, euler, and navier-stokes"
                    borderRadius="lg"
                    border="1px"
                    borderColor="gray.200"
                  />

                  <Heading size="md" color="gray.800">3.1 The four documented system choices</Heading>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>System</Th>
                          <Th>Physical interpretation</Th>
                          <Th>Good novice use</Th>
                          <Th>Main limitation</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {[
                          ['ac-euler', 'Incompressible and inviscid', 'Educational idealizations of low-speed flow', 'No viscous wall shear or boundary layer'],
                          ['ac-navier-stokes', 'Incompressible and viscous', 'Cylinder, duct, vehicle, building, fan-duct, low-speed aero', 'Does not model compressible acoustic waves as physical pressure waves'],
                          ['euler', 'Compressible and inviscid', 'Euler vortex, wave and shock benchmarks, preliminary high-speed flow', 'No viscous boundary layer, heating, or skin friction'],
                          ['navier-stokes', 'Compressible and viscous', 'High-speed aerodynamics, jets, shock/boundary-layer flow', 'More variables and potentially stricter resolution requirements'],
                        ].map((row) => (
                          <Tr key={row[0]}>
                            <Td verticalAlign="top"><Text as="code" fontSize="xs">{row[0]}</Text></Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row[1]}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row[2]}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row[3]}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>

                  <Callout title="Beginner default" status="success" icon={FaLightbulb}>
                    When the flow is low speed and walls, wakes, drag, or separation matter, start conceptually
                    with incompressible Navier–Stokes. When the flow is high speed or shock-containing, start
                    conceptually with compressible Navier–Stokes.
                  </Callout>

                  <Heading size="md" color="gray.800">3.2 A minimum setup vocabulary</Heading>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Setup item</Th>
                          <Th>Beginner question</Th>
                          <Th>Typical failure if misunderstood</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {[
                          ['Geometry/domain', 'What part is solid and what part is fluid?', 'Simulating the solid instead of the surrounding fluid, or leaving gaps.'],
                          ['Inlet/far field', 'What flow enters and from which direction?', 'Wrong angle of attack, speed, turbulence, density, or pressure.'],
                          ['Outlet', 'What information is allowed to leave?', 'Reflection, instability, or artificial pressure gradients.'],
                          ['Wall', 'Slip, no-slip, adiabatic, or isothermal?', 'Missing drag, wrong thermal response, or unphysical penetration.'],
                          ['Initial field', 'How close is the initial state to the expected solution?', 'Long transients or solver failure.'],
                          ['Order', 'How much variation is represented inside each element?', 'Too dissipative at low order or unstable/costly at high order.'],
                          ['Time step', 'How far does the solution advance each step?', 'Instability, lost temporal accuracy, or excessive cost.'],
                          ['Output sampling', 'How often are fields and statistics stored?', 'Huge files or insufficient temporal resolution.'],
                        ].map((row) => (
                          <Tr key={row[0]}>
                            <Td verticalAlign="top" fontWeight="semibold" whiteSpace="normal">{row[0]}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row[1]}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row[2]}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </VStack>
              </Box>

              <Divider />

              {/* 4-9 Field sections */}
              {FIELD_SECTIONS.map((section, idx) => (
                <React.Fragment key={section.id}>
                  <FieldSection section={section} />
                  {idx < FIELD_SECTIONS.length - 1 && <Divider />}
                </React.Fragment>
              ))}

              <Divider />

              {/* 10. Workflow */}
              <Box id="workflow">
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaTasks} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">10. A Reliable End-to-End PyFR Workflow</Heading>
                  </HStack>

                  <Image
                    src={workflowImage}
                    alt="Eight-step PyFR workflow: question, physics, geometry, mesh, configuration, run and monitor, post-process, verify and validate"
                    borderRadius="lg"
                    border="1px"
                    borderColor="gray.200"
                  />

                  <Heading size="md" color="gray.800">10.1 Start with the engineering decision</Heading>
                  <Text color="gray.700" lineHeight="tall">
                    A useful simulation begins with a question that can be answered by measurable outputs.
                    "Simulate a drone" is too vague. "Estimate the change in mean lift, drag, and pitching moment
                    when angle of attack increases from 4° to 8° at a specified Reynolds and Mach number" is
                    actionable.
                  </Text>

                  <Heading size="md" color="gray.800">10.2 Build a physics checklist</Heading>
                  <BulletList
                    items={[
                      'Fluid and property model: air, water, ideal gas, viscosity law.',
                      'Compressibility: does density change significantly or are waves/shocks important?',
                      'Viscosity: are wall shear, boundary layers, drag, separation, or mixing important?',
                      'Time dependence: is a mean solution enough, or are fluctuating loads/frequencies required?',
                      'Turbulence: laminar, transitional, turbulent, synthetic inflow, LES/implicit LES, or DNS intent?',
                      'Thermal assumptions: adiabatic or isothermal walls; is solid conduction absent?',
                      'Motion: fixed geometry, rotating reference approximation, or a capability that must be separately verified?',
                    ]}
                  />

                  <Heading size="md" color="gray.800">10.3 Geometry and domain simplification</Heading>
                  <BulletList
                    items={[
                      'Remove tiny details that do not affect the decision but force extremely small elements.',
                      'Retain edges, gaps, radii, and appendages that control separation, leakage, or wake formation.',
                      'Place far-field boundaries sufficiently far from external bodies.',
                      'Provide adequate upstream and downstream lengths for internal flows and wakes.',
                      'Use symmetry or periodicity only when the physics truly supports it.',
                    ]}
                  />

                  <Heading size="md" color="gray.800">10.4 Mesh thinking for a high-order solver</Heading>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Region</Th>
                          <Th>Why it needs attention</Th>
                          <Th>Typical refinement intent</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {[
                          ['Solid walls', 'Velocity gradients and shear form near walls.', 'Layered/curved resolution appropriate to the modelling objective.'],
                          ['Leading edges / noses', 'High curvature and strong acceleration.', 'Curved elements and locally smaller spacing.'],
                          ['Separation zones', 'Unsteady shear layers originate here.', 'Resolve geometry and the emerging shear layer.'],
                          ['Wakes', 'Forces, mixing, and noise depend on downstream vortices.', 'Maintain resolution downstream; avoid sudden coarsening.'],
                          ['Shocks', 'Discontinuous/steep gradients need stabilization and resolution.', 'Align/refine where practical and use suitable shock capturing.'],
                          ['Inlets', 'Turbulence and profile must enter without mesh artifacts.', 'Consistent spacing and sufficient injection region.'],
                          ['Mixed-element interfaces', 'Cost and partition balance can vary by element type.', 'Check quality, weights, and partition balance.'],
                        ].map((row) => (
                          <Tr key={row[0]}>
                            <Td verticalAlign="top" fontWeight="semibold" whiteSpace="normal">{row[0]}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row[1]}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row[2]}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>

                  <Heading size="md" color="gray.800">10.5 The PyFR file and command mental model</Heading>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Item</Th>
                          <Th>Purpose</Th>
                          <Th>Typical operation</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {[
                          ['.msh', 'Mesh produced by Gmsh or another compatible workflow.', 'Create and tag geometry boundaries.'],
                          ['.pyfrm', 'Imported PyFR mesh, including partitionings.', 'pyfr import; pyfr partition add/list/info.'],
                          ['.ini', 'Simulation configuration.', 'Choose system, order, integrator, boundaries, plugins, and output.'],
                          ['.pyfrs', 'PyFR solution/restart/statistics data.', 'Run/restart and retain configuration metadata.'],
                          ['.vtu/.pvtu', 'Visualization output.', 'pyfr export volume/boundary and inspect in ParaView.'],
                        ].map((row) => (
                          <Tr key={row[0]}>
                            <Td verticalAlign="top"><Text as="code" fontSize="xs">{row[0]}</Text></Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row[1]}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row[2]}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                  <Text color="gray.700">
                    Current PyFR documentation presents import, partition, run, restart, region, and export
                    commands, with parallel runs using a mesh partitioning matching the process/device count.
                  </Text>

                  <Heading size="md" color="gray.800">10.6 Monitor before trusting</Heading>
                  <BulletList
                    items={[
                      'Residual or update behaviour: is the solution stable and behaving consistently?',
                      'NaN checks and crash diagnostics: do not treat a completed write as proof of healthy physics.',
                      'Mass flow and conservation: does inlet/outlet balance make physical sense?',
                      'Force histories: have initial transients decayed and has a periodic/statistical regime formed?',
                      'Time-step statistics: is adaptive stepping behaving as intended?',
                      'Field inspection: are there nonphysical oscillations, boundary reflections, or mesh-imprinted patterns?',
                      'Hardware utilization and partition balance: is the problem large enough and balanced for the selected devices?',
                    ]}
                  />

                  <Heading size="md" color="gray.800">10.7 Post-processing outputs by decision type</Heading>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Decision</Th>
                          <Th>Primary outputs</Th>
                          <Th>Secondary checks</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {[
                          ['Reduce drag', 'Mean drag coefficient; pressure and viscous force split', 'Wake deficit, separation position, domain sensitivity'],
                          ['Increase lift', 'CL and Cp distribution', 'Moment, stall onset, 3D tip effects'],
                          ['Reduce pressure loss', 'Total-pressure loss / Δp / mass flow', 'Recirculation, flow uniformity, wall shear'],
                          ['Control vibration', 'Force/moment spectra and dominant frequency', 'Strouhal number, coherence, statistical duration'],
                          ['Reduce noise', 'Observer pressure/spectrum from FWH approximation', 'Surface placement, sample rate, flow/acoustic assumptions'],
                          ['Improve uniformity', 'Outlet profile and uniformity index', 'Mesh/order sensitivity and averaging window'],
                          ['Assess wind load', 'Mean, rms, and peaks of force/pressure', 'Inflow turbulence and duration uncertainty'],
                        ].map((row) => (
                          <Tr key={row[0]}>
                            <Td verticalAlign="top" fontWeight="semibold" whiteSpace="normal">{row[0]}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row[1]}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row[2]}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </VStack>
              </Box>

              <Divider />

              {/* 11. Practice Simulations */}
              <Box id="practice">
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaFlask} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">11. Progressive Practice Simulations</Heading>
                  </HStack>

                  <Text fontSize="lg" color="gray.700" lineHeight="tall">
                    The official test cases provide a useful backbone. The order below is selected for learning,
                    not for industrial importance.
                  </Text>

                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>#</Th>
                          <Th>Case</Th>
                          <Th>Level</Th>
                          <Th>Main concepts</Th>
                          <Th>System</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {PRACTICE_CASES.map((row) => (
                          <Tr key={row.n}>
                            <Td verticalAlign="top">{row.n}</Td>
                            <Td verticalAlign="top" fontWeight="semibold" whiteSpace="normal">{row.case}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row.level}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row.concepts}</Td>
                            <Td verticalAlign="top" whiteSpace="normal"><Text as="code" fontSize="xs">{row.system}</Text></Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>

                  <Heading size="md" color="gray.800">11.1 Learning task for every case</Heading>
                  <Card>
                    <CardBody>
                      <OrderedList spacing={3} fontSize="md" color="gray.700">
                        <ListItem>Write one paragraph predicting the expected flow before running.</ListItem>
                        <ListItem>List the governing system, major boundary conditions, Reynolds/Mach number, order, and time-step strategy.</ListItem>
                        <ListItem>Identify one scalar metric and one field visualization that answer the case question.</ListItem>
                        <ListItem>Inspect the solution for at least three nonphysical warning signs.</ListItem>
                        <ListItem>Repeat one numerical choice — mesh, order, time step, domain, or averaging duration — and quantify the change.</ListItem>
                        <ListItem>Write a conclusion that states both the result and its limitations.</ListItem>
                      </OrderedList>
                    </CardBody>
                  </Card>

                  <Callout status="info" icon={FaFlask}>
                    Many of these benchmarks are available as ready-to-fork templates on SRS. Browse them from the{' '}
                    <Link as={RouterLink} to="/examples" color="brand.500" fontWeight="semibold">Templates</Link>{' '}
                    page and launch one to work through the learning task above.
                  </Callout>
                </VStack>
              </Box>

              <Divider />

              {/* 12. Verification & Validation */}
              <Box id="verification">
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaCheckDouble} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">12. Verification, Validation, and Common Failure Modes</Heading>
                  </HStack>

                  <Heading size="md" color="gray.800">12.1 Verification versus validation</Heading>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Card bg="blue.50">
                      <CardBody>
                        <Heading size="sm" mb={2} color="blue.800">Verification</Heading>
                        <Text fontSize="sm" color="gray.700">
                          Are the equations being solved accurately enough for the chosen numerical setup?
                        </Text>
                      </CardBody>
                    </Card>
                    <Card bg="green.50">
                      <CardBody>
                        <Heading size="sm" mb={2} color="green.800">Validation</Heading>
                        <Text fontSize="sm" color="gray.700">
                          Do the equations and assumptions represent the real physical problem adequately?
                        </Text>
                      </CardBody>
                    </Card>
                  </SimpleGrid>

                  <Heading size="md" color="gray.800">12.2 Minimum credibility ladder</Heading>
                  <Card>
                    <CardBody>
                      <OrderedList spacing={3} fontSize="md" color="gray.700">
                        <ListItem><Text as="span" fontWeight="semibold">Sanity check:</Text> directions, units, magnitudes, and qualitative flow pattern are plausible.</ListItem>
                        <ListItem><Text as="span" fontWeight="semibold">Conservation check:</Text> mass and other relevant balances are acceptable.</ListItem>
                        <ListItem><Text as="span" fontWeight="semibold">Temporal check:</Text> time step and sampling rate are adequate.</ListItem>
                        <ListItem><Text as="span" fontWeight="semibold">Spatial check:</Text> mesh and polynomial-order refinement do not materially change the decision metric.</ListItem>
                        <ListItem><Text as="span" fontWeight="semibold">Domain/boundary check:</Text> far-field, inlet, outlet, and periodic assumptions do not control the answer artificially.</ListItem>
                        <ListItem><Text as="span" fontWeight="semibold">Statistical check:</Text> mean, rms, and spectra are based on enough post-transient data.</ListItem>
                        <ListItem><Text as="span" fontWeight="semibold">Validation check:</Text> compare against an analytical solution, trusted benchmark, experiment, or independently verified solver.</ListItem>
                      </OrderedList>
                    </CardBody>
                  </Card>

                  <Heading size="md" color="gray.800">12.3 Common novice mistakes</Heading>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Mistake</Th>
                          <Th>Why it is dangerous</Th>
                          <Th>Correction</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {[
                          ['Choosing Euler for a drag/separation problem', 'No viscous boundary layer or skin friction.', 'Use a Navier–Stokes system and adequate wall/wake resolution.'],
                          ['Using a steady interpretation for an unsteady wake', 'Mean values can hide large oscillatory loads.', 'Run sufficiently long and inspect force histories/spectra.'],
                          ['Increasing order on a poor mesh', 'High order does not repair invalid geometry or bad elements.', 'Improve mesh quality and curved representation first.'],
                          ['Domain too small', 'Boundaries alter pressure, blockage, and wakes.', 'Perform domain-size sensitivity.'],
                          ['Outlet too close', 'Reflections or back influence corrupt the solution.', 'Extend downstream domain and use suitable outlet conditions.'],
                          ['Saving too frequently', 'I/O dominates and storage explodes.', 'Separate restart, visualization, and sampling frequencies.'],
                          ['Saving too infrequently', 'Frequencies and transient events are missed.', 'Choose sampling from expected highest relevant frequency.'],
                          ['Reporting one run as truth', 'Numerical and modelling uncertainty are unknown.', 'Perform mesh/order/time/domain and assumption studies.'],
                          ['Confusing numerical pressure in artificial compressibility with acoustics', 'Incompressible formulation is not physical compressible wave propagation.', 'Use a compressible model or validated acoustic analogy as appropriate.'],
                          ['Ignoring startup transient', 'Statistics include initialization artifacts.', 'Define tstart after the flow reaches the desired regime.'],
                        ].map((row) => (
                          <Tr key={row[0]}>
                            <Td verticalAlign="top" fontWeight="semibold" whiteSpace="normal">{row[0]}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row[1]}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row[2]}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>

                  <Heading size="md" color="gray.800">12.4 Useful PyFR output capabilities</Heading>
                  <BulletList
                    items={[
                      'Fluid-force integration for pressure and viscous force/moment histories.',
                      'Time averaging and standard-deviation statistics for primitive-variable expressions.',
                      'Synthetic-eddy turbulence injection for selected inflow-region use cases.',
                      'Ffowcs Williams–Hawkings far-field noise approximation at specified observer points.',
                      'Sampling, residual, integration, writer, NaN-check, and in-situ visualization plugins described in the user guide.',
                    ]}
                  />
                </VStack>
              </Box>

              <Divider />

              {/* 13. Twelve-Week Plan */}
              <Box id="learning-plan">
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaCalendarAlt} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">13. Twelve-Week Learning Plan</Heading>
                  </HStack>

                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Week</Th>
                          <Th>Theme</Th>
                          <Th>Concepts and activity</Th>
                          <Th>Deliverable</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {WEEK_PLAN.map((row) => (
                          <Tr key={row.week}>
                            <Td verticalAlign="top"><Badge colorScheme="brand">{row.week}</Badge></Td>
                            <Td verticalAlign="top" fontWeight="semibold" whiteSpace="normal">{row.theme}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row.activity}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row.deliverable}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>

                  <Heading size="md" color="gray.800">13.1 Capstone simulation specification template</Heading>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Section</Th>
                          <Th>What to write</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {[
                          ['Decision question', 'The design or operational decision and the metric that will support it.'],
                          ['Geometry and domain', 'Included/excluded features; dimensions; symmetry/periodicity; domain extents.'],
                          ['Physical model', 'Fluid; compressibility; viscosity; thermal and turbulence assumptions; system choice.'],
                          ['Operating conditions', 'Speed, pressure, temperature, Reynolds number, Mach number, angle/yaw, flow rate.'],
                          ['Boundary/initial conditions', 'Every tagged boundary and the intended physical meaning.'],
                          ['Mesh strategy', 'Elements, curved regions, wall/wake/shock refinement, quality checks.'],
                          ['Numerical strategy', 'Order, time integrator, time step/adaptation, anti-aliasing, shock capturing.'],
                          ['Outputs', 'Fields, forces, samples, averages, spectra, noise observers, write frequency.'],
                          ['Credibility tests', 'Mesh/order/time/domain/averaging sensitivity and validation source.'],
                          ['Acceptance criteria', 'Quantitative conditions for declaring the result usable.'],
                        ].map((row) => (
                          <Tr key={row[0]}>
                            <Td verticalAlign="top" fontWeight="semibold" whiteSpace="normal">{row[0]}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{row[1]}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </VStack>
              </Box>

              <Divider />

              {/* 14. Glossary */}
              <Box id="glossary">
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaBook} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">14. Glossary</Heading>
                  </HStack>

                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Term</Th>
                          <Th>Beginner definition</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {GLOSSARY.map(([term, def]) => (
                          <Tr key={term}>
                            <Td verticalAlign="top" fontWeight="semibold" whiteSpace="normal">{term}</Td>
                            <Td verticalAlign="top" whiteSpace="normal">{def}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </VStack>
              </Box>

              <Divider />

              {/* Final Learning Advice */}
              <Box>
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaLightbulb} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">Final Learning Advice</Heading>
                  </HStack>

                  <Callout title="The most important habit" status="success" icon={FaLightbulb}>
                    For every simulation, separate three questions: (1) Is the physical model appropriate? (2) Is
                    the numerical solution sufficiently accurate? (3) Does the output actually answer the
                    engineering decision?
                  </Callout>

                  <Text color="gray.700" lineHeight="tall" fontSize="lg">
                    PyFR becomes valuable when these questions are answered systematically. The objective is not
                    merely to produce high-resolution contours; it is to produce defensible engineering evidence.
                  </Text>
                </VStack>
              </Box>

              <Divider />

              {/* References */}
              <Box id="references">
                <VStack align="stretch" spacing={6}>
                  <HStack>
                    <Icon as={FaLink} color="brand.500" boxSize={8} />
                    <Heading size="xl" color="gray.800">References and Official Resources</Heading>
                  </HStack>

                  <Card>
                    <CardBody>
                      <List spacing={3}>
                        {REFERENCES.map((ref, idx) => (
                          <ListItem key={ref.url} display="flex" alignItems="start">
                            <Text as="span" fontWeight="bold" color="brand.500" mr={2} minW="24px">
                              [{idx + 1}]
                            </Text>
                            <Box>
                              <Text as="span" color="gray.700">{ref.label} </Text>
                              <Link href={ref.url} isExternal color="brand.500" fontWeight="medium" wordBreak="break-all">
                                {ref.url}
                              </Link>
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    </CardBody>
                  </Card>
                </VStack>
              </Box>

              {/* Call to Action */}
              <Box bg="brand.50" p={8} borderRadius="lg" textAlign="center">
                <VStack spacing={4}>
                  <Heading size="lg" color="gray.800">Ready to put the theory into practice?</Heading>
                  <Text color="gray.600" maxW="2xl">
                    Fork a validated benchmark template and launch a ready-to-run PyFR configuration from the examples library.
                  </Text>
                  <HStack spacing={4} flexWrap="wrap" justify="center">
                    <Button as={RouterLink} to="/examples" colorScheme="brand" size="lg" leftIcon={<FaFlask />}>
                      Browse Templates
                    </Button>
                    <Button as={RouterLink} to="/tutorial" variant="outline" colorScheme="brand" size="lg" leftIcon={<FaGraduationCap />}>
                      Platform Tutorial
                    </Button>
                  </HStack>
                </VStack>
              </Box>
            </VStack>
          </Box>
        </SimpleGrid>
      </Container>

      <Footer />
    </Box>
  );
};

export default LearningGuide;
